/**
 * Automatic background prefetch engine for job snapshots.
 * 
 * "Morning Sync" — runs automatically when user opens dashboard / logs in.
 * Downloads offline snapshots for assigned jobs in priority order:
 *   1. Jobs scheduled today / next 48h
 *   2. Recently viewed jobs
 *   3. Remaining assigned jobs (Wi-Fi only)
 * 
 * Respects network conditions:
 *   - Mobile data: only today's jobs
 *   - Wi-Fi: all assigned jobs
 *   - No connection: skip
 * 
 * Singleton — safe to call multiple times (debounced).
 */

import { supabase } from "@/integrations/supabase/client";
import { downloadJobSnapshot, getSnapshot, getAllSnapshots } from "./jobSnapshotManager";
import { appState } from "./offlineDb";
import { isNative, getNetworkStatus } from "./capacitor";

export type PrefetchStatus = "idle" | "prefetching" | "done" | "error";
export type PrefetchListener = (status: PrefetchStatus, progress: PrefetchProgress) => void;

export interface PrefetchProgress {
  total: number;
  completed: number;
  failed: number;
  currentJobId: string | null;
}

let currentStatus: PrefetchStatus = "idle";
let currentProgress: PrefetchProgress = { total: 0, completed: 0, failed: 0, currentJobId: null };
let prefetching = false;
let lastPrefetchAt = 0;
const DEBOUNCE_MS = 30_000; // Don't re-prefetch within 30s
const listeners = new Set<PrefetchListener>();

// ─── Public API ──────────────────────────────────────────────

export function getPrefetchStatus(): PrefetchStatus {
  return currentStatus;
}

export function getPrefetchProgress(): PrefetchProgress {
  return { ...currentProgress };
}

export function onPrefetchChange(fn: PrefetchListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const fn of listeners) {
    try { fn(currentStatus, { ...currentProgress }); } catch { /* ignore */ }
  }
}

/**
 * Trigger automatic background prefetch. Safe to call repeatedly.
 * Called by dashboard on mount and after successful login.
 */
export async function triggerAutoPrefetch(): Promise<void> {
  if (!isNative) return;
  if (prefetching) return;
  if (!navigator.onLine) return;
  if (Date.now() - lastPrefetchAt < DEBOUNCE_MS) return;

  prefetching = true;
  lastPrefetchAt = Date.now();
  currentStatus = "prefetching";
  currentProgress = { total: 0, completed: 0, failed: 0, currentJobId: null };
  notify();

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { finish("idle"); return; }

    // Get assigned jobs
    const jobs = await fetchAssignedJobs(user.id);
    if (jobs.length === 0) { finish("done"); return; }

    // Sort by priority
    const sorted = prioritizeJobs(jobs);

    // Determine how many to prefetch based on network
    const network = getNetworkStatus();
    const isWifi = network.connectionType === "wifi";
    const limit = isWifi ? sorted.length : Math.min(sorted.length, 10); // Mobile: cap at 10

    const toPrefetch = sorted.slice(0, limit);
    currentProgress.total = toPrefetch.length;
    notify();

    // Filter out already-fresh snapshots (< 2h old)
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    const needsRefresh: typeof toPrefetch = [];
    for (const job of toPrefetch) {
      const existing = await getSnapshot(job.id);
      if (!existing || (Date.now() - existing.snapshotAt) > TWO_HOURS) {
        needsRefresh.push(job);
      } else {
        currentProgress.completed++;
      }
    }

    currentProgress.total = currentProgress.completed + needsRefresh.length;
    notify();

    // Download sequentially to avoid hammering server
    for (const job of needsRefresh) {
      if (!navigator.onLine) break; // Stop if we lose connection

      currentProgress.currentJobId = job.id;
      notify();

      try {
        await downloadJobSnapshot(job.id);
        currentProgress.completed++;
      } catch (err) {
        currentProgress.failed++;
        console.warn(`[AutoPrefetch] Failed to cache job ${job.id}:`, err);
      }
      notify();

      // Small delay between downloads to be gentle on bandwidth
      if (!isWifi) {
        await sleep(500);
      }
    }

    await appState.setNonEssential("last_auto_prefetch", String(Date.now()));
    finish("done");
  } catch (err) {
    console.error("[AutoPrefetch] Error:", err);
    finish("error");
  }
}

/**
 * Immediately snapshot a single job (called when user opens a job).
 * Non-blocking — runs in background, doesn't delay navigation.
 */
export function prefetchJobOnOpen(jobId: string): void {
  if (!isNative || !navigator.onLine) return;

  // Fire-and-forget
  getSnapshot(jobId).then(existing => {
    const ONE_HOUR = 60 * 60 * 1000;
    if (existing && (Date.now() - existing.snapshotAt) < ONE_HOUR) return; // Fresh enough
    
    console.log(`[AutoPrefetch] Caching opened job ${jobId}…`);
    downloadJobSnapshot(jobId).catch(err => {
      console.warn(`[AutoPrefetch] Failed to cache opened job ${jobId}:`, err);
    });
  }).catch(() => { /* getSnapshot already reports; never escape to unhandledrejection */ });
}

// ─── Internals ───────────────────────────────────────────────

function finish(s: PrefetchStatus) {
  currentStatus = s;
  currentProgress.currentJobId = null;
  prefetching = false;
  notify();
  console.log(`[AutoPrefetch] Finished: ${currentProgress.completed} cached, ${currentProgress.failed} failed`);
}

async function fetchAssignedJobs(userId: string): Promise<any[]> {
  // Get driver records for this user
  const { data: drivers } = await supabase
    .from("drivers")
    .select("id")
    .eq("user_id", userId);

  if (!drivers || drivers.length === 0) {
    // Also try company member role (owner/admin sees all jobs)
    const { data: members } = await supabase
      .from("company_members")
      .select("company_id")
      .eq("user_id", userId);

    if (!members || members.length === 0) return [];

    const { data: jobs } = await supabase
      .from("jobs")
      .select("id, scheduled_date, status, updated_at")
      .in("company_id", members.map(m => m.company_id))
      .in("status", ["pending", "in_progress"])
      .is("completed_at", null)
      .limit(50);

    return jobs || [];
  }

  const driverIds = drivers.map(d => d.id);

  const { data: jobs } = await supabase
    .from("jobs")
    .select("id, scheduled_date, status, updated_at")
    .in("driver_id", driverIds)
    .in("status", ["pending", "in_progress"])
    .is("completed_at", null)
    .limit(50);

  return jobs || [];
}

function prioritizeJobs(jobs: any[]): any[] {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000).toISOString().slice(0, 10);

  return [...jobs].sort((a, b) => {
    const aDate = a.scheduled_date || "9999-12-31";
    const bDate = b.scheduled_date || "9999-12-31";

    // Priority 1: Today's jobs first
    const aToday = aDate === today ? 0 : 1;
    const bToday = bDate === today ? 0 : 1;
    if (aToday !== bToday) return aToday - bToday;

    // Priority 2: Next 48 hours
    const aNext48 = aDate <= in48h ? 0 : 1;
    const bNext48 = bDate <= in48h ? 0 : 1;
    if (aNext48 !== bNext48) return aNext48 - bNext48;

    // Priority 3: In-progress before pending
    const statusOrder = { in_progress: 0, pending: 1 };
    const aStatus = statusOrder[a.status as keyof typeof statusOrder] ?? 2;
    const bStatus = statusOrder[b.status as keyof typeof statusOrder] ?? 2;
    if (aStatus !== bStatus) return aStatus - bStatus;

    // Priority 4: Most recently updated
    return (b.updated_at || "").localeCompare(a.updated_at || "");
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
